import { Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { HeaderComponent } from './components/header/header.component';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { filter, map, pairwise, Subject, takeUntil, withLatestFrom } from 'rxjs';
import { CommonModule } from '@angular/common';
import { BreadcrumbItem, MainLayoutService } from '../../services/main-layout.service';
import { DynamicIconService } from '../../services/dynamic-icon.service';
import { AccessControlService } from '../../services/access-control.service';
import { AlertNotificationService } from '../../services/alert-notification.service';
import { AccessKey } from './components/sidebar/config/access-control.config';
import { stripAppBase } from 'src/app/app-paths';



@Component({
  selector: 'app-main-layout',
  imports: [SidebarComponent, HeaderComponent, RouterOutlet, CommonModule,RouterLink],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss'
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  section: string | null = null;
  subsection: string | null = null;
  relatedCodeItem: string | null = null;
  relatedClientName: string | null = null;
  relatedBrandName: string | null = null;
  clientCompanyName: string | null = null;
  isManagementPage: boolean = false;
  managementAction: string | null = null;
  isScopeChangePage: boolean = false;
  currentIcon: string = '';
  breadcrumbs: BreadcrumbItem[] | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private renderer: Renderer2,
    private router: Router,
    private route: ActivatedRoute,
    private mainLayoutService: MainLayoutService,
    private dynamicIconService: DynamicIconService,
    private accessControlService: AccessControlService,
    private alertNotificationService: AlertNotificationService
  ) { }

  classes = [
    'layout-fixed',
    'sidebar-expand-lg',
    'bg-body-tertiary',
    'app-loaded'
  ]

  ngOnInit(): void {
    this.classes.forEach((className) => {
      this.renderer.addClass(document.body, className);
    });

    this.mainLayoutService.refreshCompany();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.updateSectionFromUrl();
      });
     this.mainLayoutService.activeProfile$.pipe(
      withLatestFrom(this.mainLayoutService.user$),
      map(([profile, user]) => ({ profile, userId: user?.userId ?? null })),
      pairwise(),
      filter(([prev, curr]) => !!prev.profile && prev.profile !== curr.profile && prev.userId === curr.userId),
      takeUntil(this.destroy$)
    ).subscribe(([, curr]) => {
      const accessKey = this.getActiveRouteAccessKey();
      if (accessKey && !this.accessControlService.hasAccess(curr.profile, accessKey)) {
        this.alertNotificationService.error('Access denied: you do not have permission to access this section.');
        this.router.navigate(['/app']);
      }
    });

    this.updateSectionFromUrl();
    this.mainLayoutService.relatedCodeItem$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.relatedCodeItem = value;
      });

    this.mainLayoutService.relatedClientName$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.relatedClientName = value;
      });

    this.mainLayoutService.relatedBrandName$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.relatedBrandName = value;
      });

    this.mainLayoutService.section$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value) {
          this.section = value;
          this.updateCurrentIcon();
        }
      });

    this.mainLayoutService.subsection$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.subsection = value;
        this.updateCurrentIcon();
      });
      
    this.mainLayoutService.isManagementPage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.isManagementPage = value;
      });
      
    this.mainLayoutService.managementAction$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.managementAction = value;
      });
      
    this.mainLayoutService.isScopeChangePage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.isScopeChangePage = value;
      });
      
    this.mainLayoutService.clientCompanyName$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.clientCompanyName = value;
      });

    this.mainLayoutService.breadcrumbs$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.breadcrumbs = value;
      });

    this.updateSectionFromUrl();
  }

  private updateSectionFromUrl(): void {
    const url = stripAppBase(this.router.url);
    const urlWithoutQuery = url.split('?')[0];
    const urlParts = urlWithoutQuery.split('/').filter(part => part !== '');
    
    this.mainLayoutService.setRelatedCodeItem(null);
    this.mainLayoutService.setRelatedClientName(null);
    this.mainLayoutService.setRelatedBrandName(null);
    this.mainLayoutService.setBreadcrumbs(null);
    
    const isUrlScopeChangePage = urlParts.length >= 2 && 
      urlParts[0].toLowerCase() === 'proposals' && 
      urlParts[1].toLowerCase() === 'change-of-scope';
      
    if (isUrlScopeChangePage) {
      this.mainLayoutService.setIsScopeChangePage(true);
    }
    
    if (urlParts.length === 0) {
      this.mainLayoutService.setSection('Dashboard');
      this.mainLayoutService.setSubsection(null);
      this.mainLayoutService.setIsManagementPage(false);
      this.mainLayoutService.setManagementAction(null);
      return;
    }
    
    const settingsIndex = urlParts.indexOf('settings');
    
    if (settingsIndex !== -1 && settingsIndex + 1 < urlParts.length) {
      const mainSection = this.formatSectionName(urlParts[0]);
      const subsection = this.formatSectionName(urlParts[settingsIndex + 1]);
      
      this.mainLayoutService.setSection(mainSection);
      this.mainLayoutService.setSubsection(subsection);
      this.mainLayoutService.setIsManagementPage(false);
      this.mainLayoutService.setManagementAction(null);
    } else {
      const mainSection = this.formatSectionName(urlParts[0]);
      this.mainLayoutService.setSection(mainSection);
      
      if (urlParts.length > 1) {
        const action = urlParts[1].toLowerCase();
        
        const isIdOrUuid = !isNaN(Number(action)) || action.match(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/);
        const isActionWord = ['create', 'update', 'detail'].includes(action);
        
       if (isIdOrUuid || isActionWord) {
          this.mainLayoutService.setIsManagementPage(true);
          
          if (action === 'create') {
            this.mainLayoutService.setManagementAction('Create');
          } else if (action === 'update') {
            this.mainLayoutService.setManagementAction('Update');
          } else if (urlParts.length > 2 && urlParts[2] === 'update') {
            this.mainLayoutService.setManagementAction('Update');
          } else if (isIdOrUuid || action === 'detail') {
            this.mainLayoutService.setManagementAction('Detail');
          } else {
            this.mainLayoutService.setManagementAction('Management');
          }
          
          this.mainLayoutService.setSubsection(null);
        } else {
          this.mainLayoutService.setSubsection(null);
          this.mainLayoutService.setIsManagementPage(false);
          this.mainLayoutService.setManagementAction(null);
        }
      } else {
        this.mainLayoutService.setSubsection(null);
        this.mainLayoutService.setIsManagementPage(false);
        this.mainLayoutService.setManagementAction(null);
      }
    }
  }

  private getActiveRouteAccessKey(): AccessKey | undefined {
    let snapshot = this.router.routerState.snapshot.root;
    while (snapshot.firstChild) {
      snapshot = snapshot.firstChild;
    }
    return snapshot.data?.['accessKey'] as AccessKey | undefined;
  }

  private formatSectionName(sectionName: string): string {
    return sectionName
      .replace(/-([a-z])/g, (_, letter) => ' ' + letter.toUpperCase())
      .replace(/^([a-z])/, (_, letter) => letter.toUpperCase());
  }

  private updateCurrentIcon(): void {
    const icon = this.dynamicIconService.getIconForSection(this.section, this.subsection);
    this.currentIcon = icon;
  }

  ngOnDestroy(): void {
    this.classes.forEach((className) => {
      this.renderer.removeClass(document.body, className);
    });
    this.mainLayoutService.setIsScopeChangePage(false);
    this.destroy$.next();
    this.destroy$.complete();
  }
}